/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useCallback, useEffect, useRef } from "react";

import { scrollIntoView } from "../../../viewModel/utility/scrollIntoView";
import { OVERRIDABLE_FEATURE_STAGES } from "../../../viewModel/featureActivation";
import type { FeatureStage } from "../../../viewModel/featureActivation";
import {
  getActivationControlIndex,
  getStageControlIndex
} from "../../../viewModel/featureOverride";
import type { TFeatureOverrideRow } from "../../../viewModel/featureOverride";
import type { FeatureOverrideScreenProps } from "./FeatureOverrideScreenProps";

import "./FeatureOverrideScreen.scss";

const TITLE_ID: string = "feature-override-title";
const NOTE_ID: string = "feature-override-note";

/** The three activation choices, in the order they cycle and render. */
const ACTIVATION_OPTIONS: ReadonlyArray<{
  label: string;
  value: boolean | null;
}> = [
  { label: "Default", value: null },
  { label: "ON", value: true },
  { label: "OFF", value: false }
];

/**
 * The stage choices, in the order they cycle and render: DEFAULT first, then
 * the overridable stages. `RELEASED` is absent by design — it is the only
 * production-visible stage and no debug setting may grant it.
 */
const STAGE_OPTIONS: ReadonlyArray<{
  label: string;
  value: FeatureStage | null;
}> = [
  { label: "Default", value: null },
  ...OVERRIDABLE_FEATURE_STAGES.map(
    (stage: FeatureStage): { label: string; value: FeatureStage | null } => ({
      label: stage,
      value: stage
    })
  )
];

/** The two choices the Video Debug Overlay switch cycles between. */
const OVERLAY_OPTIONS: ReadonlyArray<{ label: string; value: boolean }> = [
  { label: "OFF", value: false },
  { label: "ON", value: true }
];

/** Keeps the focus ring clear of the scroll edges as rows scroll into view. */
const SCROLL_MARGIN_PX: number = 24;

// Shared class names. Extracted because every focusable control on this
// screen — both per-feature dropdowns and the trailing overlay switch —
// composes the same base + modifier pair.
const ROW_CLASS: string = "feature-override__row";
const ROW_FOCUSED_CLASS: string = " feature-override__row--focused";
const OPTION_CLASS: string = "feature-override__option";
const OPTION_SELECTED_CLASS: string = " feature-override__option--selected";

/**
 * FeatureOverrideScreen — DEBUG-ONLY Feature Configuration UI.
 *
 * Opened by the hidden `123456` code and never reachable in normal use; see
 * `useFeatureOverrideCode`. Purely presentational: all state lives in
 * `viewModel/featureOverride` and is wired up by `FeatureOverrideContainer`.
 *
 * Each feature exposes two independent sections:
 *
 *   - **Stage** — Default / DISABLED / IN_DEVELOPMENT / IN_TESTING. The registry
 *     stage and the resulting effective stage are printed underneath, so an
 *     active override is obvious at a glance.
 *   - **Activation Override** — Default / ON / OFF, unchanged behaviour; it
 *     still wins over everything else, including the stage.
 *
 * A trailing **Video Debug Overlay** switch also lives here. It is not a
 * feature override — it is the persisting activation path for the playback
 * diagnostics panel (proposal §4 method ②), placed on this screen because
 * this is the app's developer settings surface and the user-facing Settings
 * list is Figma-specified.
 *
 * D-pad LEFT/RIGHT cycles the focused control's choice and UP/DOWN walks the
 * flat control list (two per feature, then the two reset buttons, then the
 * overlay switch) — all handled by `useFeatureOverrideInput`. The pointer
 * handlers here mirror that for browser/webOS.
 */
const FeatureOverrideScreen: FunctionComponent<FeatureOverrideScreenProps> = (
  props: FeatureOverrideScreenProps
): JSX.Element => {
  const {
    rows,
    focusedIndex,
    resetStageIndex,
    resetFeatureIndex,
    videoDebugOverlayIndex,
    isVideoDebugOverlayEnabled,
    onFocusControl,
    onSelectValue,
    onSelectStage,
    onResetAllStages,
    onResetAll,
    onSetVideoDebugOverlay
  } = props;

  const focusedControlRef = useRef<HTMLDivElement | null>(null);

  // Keep the D-pad-focused control visible when the list overflows the dialog.
  useEffect((): void => {
    if (focusedControlRef.current !== null) {
      scrollIntoView(focusedControlRef.current, {
        behavior: "smooth",
        block: "nearest",
        margin: SCROLL_MARGIN_PX
      });
    }
  }, [focusedIndex]);

  const handleResetStageFocus = useCallback((): void => {
    onFocusControl(resetStageIndex);
  }, [onFocusControl, resetStageIndex]);

  const handleResetFeatureFocus = useCallback((): void => {
    onFocusControl(resetFeatureIndex);
  }, [onFocusControl, resetFeatureIndex]);

  const handleOverlayFocus = useCallback((): void => {
    onFocusControl(videoDebugOverlayIndex);
  }, [onFocusControl, videoDebugOverlayIndex]);

  return (
    <div className="feature-override">
      <div className="feature-override__scrim" aria-hidden="true" />
      <div
        className="feature-override__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-describedby={NOTE_ID}
      >
        <h2 id={TITLE_ID} className="feature-override__title">
          Feature Debug Configuration
        </h2>

        <p id={NOTE_ID} className="feature-override__note">
          Development/testing only. Stage changes how the feature is evaluated;
          Activation Override forces the result and always wins. Default uses
          the normal feature activation rules.
        </p>

        <div className="feature-override__list">
          {rows.map(
            (row: TFeatureOverrideRow, rowIndex: number): JSX.Element => {
              const stageControlIndex: number = getStageControlIndex(rowIndex);
              const activationControlIndex: number =
                getActivationControlIndex(rowIndex);
              const isStageFocused: boolean =
                focusedIndex === stageControlIndex;
              const isActivationFocused: boolean =
                focusedIndex === activationControlIndex;
              const isStageOverridden: boolean = row.stageOverride !== null;

              return (
                <div key={row.feature} className="feature-override__feature">
                  <span className="feature-override__feature-name">
                    {row.feature}
                  </span>

                  <div
                    ref={isStageFocused ? focusedControlRef : null}
                    className={
                      ROW_CLASS + (isStageFocused ? ROW_FOCUSED_CLASS : "")
                    }
                  >
                    <div className="feature-override__row-label">
                      <span className="feature-override__section-name">
                        Stage
                      </span>
                      <span className="feature-override__stage-meta">
                        {"Registry Stage: " + row.registryStage}
                      </span>
                      <span
                        className={
                          "feature-override__stage-meta" +
                          (isStageOverridden
                            ? " feature-override__stage-meta--overridden"
                            : "")
                        }
                      >
                        {"Current Effective Stage: " + row.effectiveStage}
                      </span>
                    </div>

                    <div className="feature-override__options">
                      {STAGE_OPTIONS.map(
                        (option: {
                          label: string;
                          value: FeatureStage | null;
                        }): JSX.Element => {
                          const isSelected: boolean =
                            row.stageOverride === option.value;
                          return (
                            <button
                              key={option.label}
                              type="button"
                              className={
                                OPTION_CLASS +
                                (isSelected ? OPTION_SELECTED_CLASS : "")
                              }
                              aria-pressed={isSelected}
                              onFocus={(): void =>
                                onFocusControl(stageControlIndex)
                              }
                              onClick={(): void =>
                                onSelectStage(rowIndex, option.value)
                              }
                            >
                              {option.label}
                            </button>
                          );
                        }
                      )}
                    </div>
                  </div>

                  <div
                    ref={isActivationFocused ? focusedControlRef : null}
                    className={
                      ROW_CLASS + (isActivationFocused ? ROW_FOCUSED_CLASS : "")
                    }
                  >
                    <div className="feature-override__row-label">
                      <span className="feature-override__section-name">
                        Activation Override
                      </span>
                    </div>

                    <div className="feature-override__options">
                      {ACTIVATION_OPTIONS.map(
                        (option: {
                          label: string;
                          value: boolean | null;
                        }): JSX.Element => {
                          const isSelected: boolean =
                            row.override === option.value;
                          return (
                            <button
                              key={option.label}
                              type="button"
                              className={
                                OPTION_CLASS +
                                (isSelected ? OPTION_SELECTED_CLASS : "")
                              }
                              aria-pressed={isSelected}
                              onFocus={(): void =>
                                onFocusControl(activationControlIndex)
                              }
                              onClick={(): void =>
                                onSelectValue(rowIndex, option.value)
                              }
                            >
                              {option.label}
                            </button>
                          );
                        }
                      )}
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </div>

        <div className="feature-override__actions">
          <button
            type="button"
            className={
              "feature-override__reset" +
              (focusedIndex === resetStageIndex
                ? " feature-override__reset--focused"
                : "")
            }
            onFocus={handleResetStageFocus}
            onClick={onResetAllStages}
          >
            Reset All Stage Overrides
          </button>
          <button
            type="button"
            className={
              "feature-override__reset" +
              (focusedIndex === resetFeatureIndex
                ? " feature-override__reset--focused"
                : "")
            }
            onFocus={handleResetFeatureFocus}
            onClick={onResetAll}
          >
            Reset All Feature Overrides
          </button>
        </div>

        {/*
          Device-level developer switch rather than a per-feature override,
          so it sits after the resets in its own section. This is the
          persisting activation path for the Video Debug Overlay
          (proposal §4 method ②) — the D-pad sequence and the playback
          menu toggle the same overlay for the current session only.
        */}
        <div
          className={
            ROW_CLASS +
            (focusedIndex === videoDebugOverlayIndex ? ROW_FOCUSED_CLASS : "")
          }
          ref={
            focusedIndex === videoDebugOverlayIndex ? focusedControlRef : null
          }
        >
          <div className="feature-override__row-label">
            <span className="feature-override__section-name">
              Video Debug Overlay
            </span>
            <span className="feature-override__stage-meta">
              Persists across reloads. Also: UP UP DOWN DOWN RIGHT, or
              Ctrl+Shift+D.
            </span>
          </div>

          <div className="feature-override__options">
            {OVERLAY_OPTIONS.map(
              (option: { label: string; value: boolean }): JSX.Element => {
                const isSelected: boolean =
                  isVideoDebugOverlayEnabled === option.value;
                return (
                  <button
                    key={option.label}
                    type="button"
                    className={
                      OPTION_CLASS + (isSelected ? OPTION_SELECTED_CLASS : "")
                    }
                    aria-pressed={isSelected}
                    onFocus={handleOverlayFocus}
                    onClick={(): void => onSetVideoDebugOverlay(option.value)}
                  >
                    {option.label}
                  </button>
                );
              }
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureOverrideScreen;
